import { dragScroll, fetchSections } from './global.js';

/**
 * <variant-picker>
 * Resout la variante a partir des options cochees, met a jour l'URL,
 * l'input cache du formulaire et rafraichit le prix via la Section
 * Rendering API. Ne suppose aucun ordre d'option.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variants]');
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.sectionId = this.dataset.section;
    this.addEventListener('change', () => this.onChange());
  }

  get selectedOptions() {
    return [...this.querySelectorAll('input[type="radio"]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
  }

  findVariant() {
    const selected = this.selectedOptions;
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );
  }

  async onChange() {
    const variant = this.findVariant();
    this.markUnavailable();

    if (!variant) {
      this.setSubmitState(null);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    const input = document.querySelector('[data-variant-input]');
    if (input) input.value = variant.id;

    this.setSubmitState(variant);

    try {
      const sections = await fetchSections([this.sectionId]);
      const html = sections[this.sectionId];
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const price = parsed.querySelector('.rc-price');
      const current = document.querySelector('.rc-product__panel .rc-price');
      if (price && current) current.replaceWith(price);
    } catch (error) {
      console.warn('[variant-picker]', error);
    }
  }

  /** Grise les valeurs qui ne menent a aucune combinaison achetable. */
  markUnavailable() {
    const available = this.variants.filter((v) => v.available);
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const reachable = available.some((v) => v.options[position] === input.value);
      input.toggleAttribute('data-unavailable', !reachable);
    });
  }

  setSubmitState(variant) {
    const button = document.querySelector('.rc-product__submit');
    const label = button?.querySelector('[data-add-label]');
    if (!button || !label) return;

    const unavailable = !variant || !variant.available;
    button.disabled = unavailable;
    label.textContent = unavailable
      ? window.rcStrings?.soldOut || 'Épuisé'
      : window.rcStrings?.addToCart || 'Ajouter au panier';
  }
}

/**
 * <product-gallery>
 * Le rail defile nativement : le balayage tactile et le clavier marchent
 * sans code. Le JS ajoute le glisser a la souris, le survol des miniatures,
 * et tient a jour la miniature active en observant la position reelle du
 * rail plutot qu'en la supposant.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-gallery-rail]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.rail) return;

    this.releaseDrag = dragScroll(this.rail);
    if (this.thumbs.length === 0) return;

    // Le survol previsualise, le clic confirme. Reserve aux pointeurs qui
    // survolent vraiment : sur un ecran tactile, `hover` se declenche au
    // premier contact et vole le geste.
    const canHover = window.matchMedia('(hover: hover)').matches;

    this.thumbs.forEach((thumb) => {
      const show = () => this.show(Number(thumb.dataset.galleryThumb));
      thumb.addEventListener('click', show);
      if (canHover) thumb.addEventListener('mouseenter', show);
    });

    // La miniature active suit la diapositive reellement visible, qu'on y
    // soit arrive par la miniature ou par un balayage du doigt.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.slides.indexOf(entry.target);
          this.thumbs.forEach((thumb, i) => {
            if (i === index) {
              thumb.setAttribute('aria-current', 'true');
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
        });
      },
      { root: this.rail, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  /**
   * Amene une diapositive en tete du rail.
   *
   * On calcule le decalage plutot que d'appeler `scrollIntoView` : celui-ci
   * remonte la chaine des ancetres et fait aussi bouger la page, et il est
   * sans effet fiable sur un conteneur en grille a colonnes implicites.
   */
  show(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const offset = slide.getBoundingClientRect().left
      - this.rail.getBoundingClientRect().left
      + this.rail.scrollLeft;

    // Deux pieges se cumulent ici. `scroll-snap-type: mandatory` ramene le
    // rail sur sa diapositive courante des qu'on le deplace par programme,
    // et toucher a cette propriete annule une animation de defilement en
    // cours. Le saut est donc fait d'un bloc, aimantation suspendue et
    // lissage desactive, puis les deux sont retablis immediatement : on
    // atterrit pile sur un point d'ancrage, il n'y a rien a re-aimanter.
    //
    // Un clic sur une miniature est une selection directe, pas un
    // parcours : l'instantane est le bon comportement. Le lissage reste
    // la ou il compte, sur le balayage au doigt et le glisser a la souris.
    const { scrollSnapType, scrollBehavior } = this.rail.style;
    this.rail.style.scrollSnapType = 'none';
    this.rail.style.scrollBehavior = 'auto';
    this.rail.scrollLeft = offset;
    this.rail.style.scrollBehavior = scrollBehavior;
    this.rail.style.scrollSnapType = scrollSnapType;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.releaseDrag?.();
  }
}

/**
 * <quantity-input>
 * Deux boutons autour d'un champ nombre. Le champ reste un `input[type=number]`
 * : sans JavaScript, il est toujours saisissable au clavier.
 */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-step]');
      if (!button) return;
      const min = Number(this.input.min) || 1;
      const next = Number(this.input.value) + Number(button.dataset.quantityStep);
      this.input.value = Math.max(min, next);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
